import * as echarts from 'echarts'
import lodash from 'lodash'
import React from 'react'
import { ChartArea, classToFComponent } from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'
import { JSX } from 'react/jsx-runtime'

import { EChartViewClass } from '../../../the-consumer-market/views/helpers/base-echart'
import { BillingChartScope } from '../bl_scopes'
import BillingCard, { BillingCardHeader } from './bl_card'
import type { BillingPeriodRow } from '../bl_types'
import { formatCurrency, formatMonthLabel, formatNumber } from 'src/utils/formatter-utils'
import { formatNumberToCompactShortForm } from '../../../the-consumer-market/views/helpers/tcm_view_utils'
import { LifesHubColors } from 'src/utils/views'

const LOG = Logger.get('BL-CHART-MONTHLY')

const INK_SECONDARY = LifesHubColors.muted
const INK_SELECTED = LifesHubColors.green800
const AXIS_LINE = LifesHubColors.border
const GRID_LINE = LifesHubColors.gridLine

const EXCEEDED_COLOR = LifesHubColors.chartEmphasis
const VALUE_COLOR = LifesHubColors.warning
const INVOICED_COLOR = LifesHubColors.blue
const COURTESY_COLOR = LifesHubColors.purple

const VALUE_STACK = 'valor'

const EXCEEDED_SERIES = 'Registros excedentes'

const DIMMED = 0.25

const EXCEEDED_HEADROOM = 1.1
const VALUE_HEADROOM = 1.4

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        alignSelf: 'stretch',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minHeight: 200,
        cursor: 'pointer'
    }
})

type ChartMonthlyProps = {
    className?: string
    style?: React.CSSProperties
    scope: BillingChartScope
}

class ChartMonthlyViewClass extends EChartViewClass<ChartMonthlyProps> {
    scope!: BillingChartScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState(props: ChartMonthlyProps) {
        this.scope = props.scope
        this.classes = useStyles().classes
        this.chartData = buildChartData(this.scope)
        this.loaded = this.scope.loaded
        super.onSyncState(props)
    }

    override render({ className, style, scope }: ChartMonthlyProps): React.JSX.Element {
        return (
            <BillingCard elevation={0} className={className} style={style}>
                <BillingCardHeader title={scope.caption || 'Excedente e valor por competência'} />
                <ChartArea>{super.render({ className: this.classes.view, scope })}</ChartArea>
            </BillingCard>
        )
    }

    protected override onBindChartEvents(chart: echarts.ECharts) {
        chart.off('click', this.emitClickOnAxisLabel)
        chart.on('click', this.emitClickOnAxisLabel)

        chart.getZr().off('click', this.emitClickOnPlotArea)
        chart.getZr().on('click', this.emitClickOnPlotArea)
    }

    readonly emitClickOnPlotArea = (evt: { offsetX: number; offsetY: number }) => {
        const { chart } = this
        const { rows } = this.scope

        if (!chart || rows.length === 0) {
            return
        }

        const point = [evt.offsetX, evt.offsetY]

        if (!chart.containPixel('grid', point)) {
            return
        }

        const [index] = chart.convertFromPixel({ seriesIndex: 0 }, point)
        const row = rows[lodash.clamp(Math.round(index), 0, rows.length - 1)]

        if (row) {
            this.scope.onPeriodClicked(row.period).catch(LOG.caught)
        }
    }

    readonly emitClickOnAxisLabel = (evt: echarts.ECElementEvent) => {
        if (evt.componentType !== 'xAxis') {
            return
        }

        const row = this.scope.rows.find((item) => formatMonthLabel(item.period) === evt.value)

        if (row) {
            this.scope.onPeriodClicked(row.period).catch(LOG.caught)
        }
    }
}

export const BillingChartMonthlyView = classToFComponent(ChartMonthlyViewClass, React)
export default BillingChartMonthlyView

function buildChartData(scope: BillingChartScope): echarts.EChartsOption {
    const { selectedPeriod } = scope
    const isDimmed = (period: string) => !!selectedPeriod && period !== selectedPeriod

    const labels = scope.rows.map((row) => ({
        value: formatMonthLabel(row.period),
        textStyle: {
            color: row.period === selectedPeriod ? INK_SELECTED : INK_SECONDARY,
            fontWeight: (row.period === selectedPeriod ? 'bold' : 'normal') as 'bold' | 'normal'
        }
    }))

    const serie = (pick: (row: BillingPeriodRow) => number) =>
        scope.rows.map((row) => ({
            value: pick(row),
            itemStyle: { opacity: isDimmed(row.period) ? DIMMED : 1 }
        }))

    const maxExcedente = niceCeil(Math.max(0, ...scope.rows.map((row) => row.qtdExcedente)) * EXCEEDED_HEADROOM)
    const maxValor = niceCeil(Math.max(0, ...scope.rows.map((row) => row.valor)) * VALUE_HEADROOM)

    return {
        grid: { left: 8, right: 12, top: 32, bottom: 4, containLabel: true },
        legend: { top: 0, type: 'scroll', textStyle: { color: INK_SECONDARY } },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: buildTooltip
        },
        xAxis: {
            type: 'category',
            data: labels,
            axisTick: { show: false },
            axisLine: { lineStyle: { color: AXIS_LINE } },
            axisLabel: { color: INK_SECONDARY },
            triggerEvent: true
        },
        yAxis: [
            {
                type: 'value',
                name: 'Excedente',
                max: maxExcedente || undefined,
                splitLine: { lineStyle: { color: GRID_LINE } },
                axisLabel: { color: INK_SECONDARY, formatter: (value: number) => formatNumberToCompactShortForm(value) }
            },
            {
                type: 'value',
                name: 'Valor',
                max: maxValor || undefined,
                splitLine: { show: false },
                axisLabel: { color: INK_SECONDARY, formatter: (value: number) => formatCurrency(Number(value)) }
            }
        ],
        series: [
            {
                name: EXCEEDED_SERIES,
                type: 'line',
                data: serie((row) => row.qtdExcedente),
                yAxisIndex: 0,
                smooth: true,
                symbolSize: 6,
                z: 3,
                itemStyle: { color: EXCEEDED_COLOR },
                lineStyle: { color: EXCEEDED_COLOR }
            },
            {
                name: 'Faturado',
                type: 'bar',
                stack: VALUE_STACK,
                data: serie((row) => row.valorFaturado),
                yAxisIndex: 1,
                barMaxWidth: 32,
                z: 2,
                itemStyle: { color: INVOICED_COLOR }
            },
            {
                name: 'Cortesia',
                type: 'bar',
                stack: VALUE_STACK,
                data: serie((row) => row.valorCortesia),
                yAxisIndex: 1,
                barMaxWidth: 32,
                z: 2,
                itemStyle: { color: COURTESY_COLOR }
            },
            {
                name: 'A faturar',
                type: 'bar',
                stack: VALUE_STACK,
                data: serie((row) => row.valorAberto),
                yAxisIndex: 1,
                barMaxWidth: 32,
                z: 2,
                itemStyle: { color: VALUE_COLOR }
            }
        ]
    }
}

function niceCeil(value: number): number {
    if (!Number.isFinite(value) || value <= 0) {
        return 0
    }

    const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
    return Math.ceil(value / magnitude) * magnitude
}

type TooltipItem = {
    seriesName?: string
    marker?: string
    value?: unknown
    axisValueLabel?: string
}

function buildTooltip(params: unknown): string {
    const items = (Array.isArray(params) ? params : [params]) as TooltipItem[]

    if (items.length === 0) {
        return ''
    }

    const contagem = items.filter((item) => item.seriesName === EXCEEDED_SERIES)
    const valores = items.filter((item) => item.seriesName !== EXCEEDED_SERIES)

    const linhas = [
        `<div style="margin-bottom:6px;font-weight:700;color:${LifesHubColors.ink}">${
            items[0].axisValueLabel ?? ''
        }</div>`,
        ...contagem.map((item) => tooltipRow(item, formatNumber(Number(item.value ?? 0))))
    ]

    if (valores.length > 0) {
        if (contagem.length > 0) {
            linhas.push('<div style="height:10px"></div>')
        }

        linhas.push(...valores.map((item) => tooltipRow(item, formatCurrency(Number(item.value ?? 0)))))

        const total = valores.reduce((acc, item) => acc + Number(item.value ?? 0), 0)
        linhas.push(tooltipTotal(formatCurrency(total)))
    }

    return linhas.join('')
}

function tooltipRow(item: TooltipItem, value: string): string {
    return (
        '<div style="display:flex;align-items:center;justify-content:space-between;gap:24px">' +
        `<span>${item.marker ?? ''}${item.seriesName ?? ''}</span>` +
        `<span style="font-weight:600">${value}</span>` +
        '</div>'
    )
}

function tooltipTotal(value: string): string {
    return (
        '<div style="display:flex;align-items:center;justify-content:space-between;gap:24px;' +
        'margin-top:6px;font-weight:700">' +
        '<span>Total</span>' +
        `<span>${value}</span>` +
        '</div>'
    )
}
